import {
  AlertCircleIcon,
  AttachmentCircleIcon,
  CheckmarkCircle02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { isScreeningAttentionVisible } from 'helpers/screeningVerificationBadge';

export const REFERENCE_CHECK_TYPES = ['reference', 'custom_reference'];

export const ALERT_BADGES = {
  result: { icon: AttachmentCircleIcon, className: 'surface-error' },
  ipMatch: { icon: AlertCircleIcon, className: 'surface-error' },
  flagHigh: { icon: AlertCircleIcon, className: 'surface-error' },
  flagMedium: { icon: AlertCircleIcon, className: 'surface-warning' },
  flagLow: { icon: AlertCircleIcon, className: 'surface-info' },
  attention: { icon: AlertCircleIcon, className: 'surface-warning' },
  not_approved: { icon: AlertCircleIcon, className: 'surface-error' },
  needs_review: { icon: AlertCircleIcon, className: 'surface-warning' },
  approved: { icon: CheckmarkCircle02Icon, className: 'surface-success' },
};

const REFEREE_ALERT_ORDER = [
  'ipMatch',
  'flagHigh',
  'flagMedium',
  'flagLow',
  'not_approved',
  'needs_review',
  'approved',
];

const attentionCheck = check => ({
  canceled: check.status === 'canceled',
  attention_flag: check.attention_flag,
  completed_at: check.status === 'completed' ? true : null,
  result_flag: check.result_flag,
});

const flagAlertType = severity =>
  severity ? 'flag' + severity[0].toUpperCase() + severity.slice(1) : null;

const userReviewAlertType = check =>
  check.status === 'completed' && ALERT_BADGES[check.user_review_status]
    ? check.user_review_status
    : null;

const isCanceled = check => check.status === 'canceled';

export const getCheckAlertType = ({ check, checkType, severity }) => {
  if (isCanceled(check)) return null;
  if (check.result_flag && check.status === 'completed') return 'result';
  if (severity === 'high') return 'flagHigh';
  if (isScreeningAttentionVisible(attentionCheck(check), checkType)) return 'attention';
  if (severity === 'medium') return 'flagMedium';
  return userReviewAlertType(check) || flagAlertType(severity);
};

export const getRefereeAlertType = ({ check, referee, severity }) => {
  if (isCanceled(check)) return null;
  if (referee.warning_flag && referee.status === 'completed') return 'ipMatch';
  return flagAlertType(severity) || userReviewAlertType(check);
};

export const getWorstRefereeAlertType = ({ check, referees, severityOf }) => {
  if (isCanceled(check)) return null;
  const alertTypes = referees.map(referee =>
    getRefereeAlertType({ check, referee, severity: severityOf(referee) }),
  );
  return (
    REFEREE_ALERT_ORDER.find(alertType => alertTypes.includes(alertType)) ||
    userReviewAlertType(check)
  );
};

export const CheckAlertBadge = ({ type, size = 16, classNames = {} }) => {
  const badge = ALERT_BADGES[type];
  if (!badge) return null;

  return (
    <HugeiconsIcon
      icon={badge.icon}
      size={size}
      strokeWidth={2}
      className={cn(
        'white-text',
        'u-absolute',
        classNames[type] || badge.className,
        'u-border-radius--half',
      )}
      style={{ top: '-10px', right: '-8px', zIndex: 10, padding: '1px' }}
    />
  );
};

export const getAlertLabel = (type, t, checkType) => {
  switch (type) {
    case 'result':
      return checkType?.true_flag_label || 'This check has results';
    case 'attention':
      return checkType?.attention_flag_config?.attention_label || 'Needs attention';
    case 'ipMatch':
    case 'flagHigh':
    case 'flagMedium':
    case 'flagLow':
      return t(
        'flagSeverity.' +
          {
            ipMatch: 'ipMatch',
            flagHigh: 'highFlag',
            flagMedium: 'mediumFlag',
            flagLow: 'lowFlag',
          }[type],
      );
    default:
      return null;
  }
};

export const getAlertLabels = ({ check, checkType, severity, ipMatch = false, t }) => {
  const labels = [];
  if (isCanceled(check)) return labels;
  if (ipMatch) labels.push(getAlertLabel('ipMatch', t, checkType));
  if (check.result_flag && check.status === 'completed') {
    labels.push(getAlertLabel('result', t, checkType));
  }
  if (isScreeningAttentionVisible(attentionCheck(check), checkType)) {
    labels.push(getAlertLabel('attention', t, checkType));
  }
  if (severity) labels.push(getAlertLabel(flagAlertType(severity), t, checkType));
  return labels;
};
